import '../css/spinner.css'
import '../css/loader.css'
import AppContext from  '../context/AppContext.js'
import HistoryContext from "../context/HistoryContext.js"
import ParamsContext from "../context/ParamsContext.js"
import CardsContext from "../context/CardsContext.js"
import React, {useEffect, useContext, useRef, useState } from "react";
import readCards from "../io/readCards"
import readParams from "../io/readParams"
import readHistory from "../io/readHistory"
import readSheetTitles from "../io/readSheetTitles"
import {syncActiveColumnWithHistory} from "../io/cardsSheet"
import { writeParamsSheet } from "../io/paramsSheet";
import { useGoogleLogin } from '@react-oauth/google';
import GoogleButton from './GoogleButton'
import {
  GOOGLE_APP_ID,
  GOOGLE_CLIENT_ID,
  GOOGLE_DRIVE_FILE_SCOPE,
  GOOGLE_PICKER_API_KEY,
  getMaskedGooglePickerApiKey,
} from "../io/googleDriveConfig.js"
import {
  exchangeGoogleAuthCode,
  refreshGoogleAccessToken,
} from "../io/googleAuthSession.js"
import { loadGooglePickerApi, showSpreadsheetPicker } from "../io/googlePicker.js"
import {
  buildRecentDecksStorageValue,
  parseRecentDecks,
  upsertRecentDeck,
} from "../io/recentDecks.js"
import { initFsrsBrowser } from "../model/fsrsScheduler.js";

function assert(condition, message) {                                                                
  if (!condition) {                                                                                  
    throw new Error(message || "Assertion failed");                                                  
  }                                                                                                  
} 

const hasTruthyQueryValue = (value) =>
  value != null && ["1", "true", "yes", "on"].includes(String(value).toLowerCase());

const shouldTrainImmediately = () => {
  try {
    return hasTruthyQueryValue(
      new URLSearchParams(window.location.search).get("train_immediately")
    );
  } catch (error) {
    return false;
  }
};

const Spinner  = () => {
  return (
    <div className="lds-ring">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div> 
  )
}


export const Loader = () => {
  const {cardSelector, userGuid, setScreen, setSpreadsheetAccessData} = useContext(AppContext)
  const {setCards, setPreselectedCard, setPCorrect} = useContext(CardsContext)
  const {setParams} = useContext(ParamsContext)
  const {setHistory, setReview} = useContext(HistoryContext)
  const pendingActionRef = useRef({ type: null, deck: null });
  
  const [spinnerActive, setSpinnerActive]  = useState(false);
  const [, setTryAgain] = useState(false);
 
  const [recentSheets, setRecentSheets] = useState([])
  const [loadText, setLoadText] = useState('')
  useEffect( () => {
    setRecentSheets(parseRecentDecks(window.localStorage.getItem('recent')));
  }, [])
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.info("Google Picker config", {
        developerKeyPreview: getMaskedGooglePickerApiKey(),
        appId: GOOGLE_APP_ID,
        clientId: GOOGLE_CLIENT_ID,
      });
    }

    if (!GOOGLE_PICKER_API_KEY) {
      return;
    }

    loadGooglePickerApi()
      .then(() => undefined)
      .catch((error) => {
        console.error("Failed to preload Google Picker", error);
      });

    initFsrsBrowser().catch((error) => {
      console.error("Failed to preload fsrs-browser", error);
    });
  }, []);
  const recentSheetElements = recentSheets.map( (sheet_element, x) => {
    const name = sheet_element.title;
    return (
      <div key={x} className='sheet-element' onClick={() => {
        if (!userGuid) {
          setLoadText("Still initializing your local session. Try again in a second.");
          return;
        }
        beginGoogleSession({
          type: "recent",
          deck: sheet_element,
        });
      }}>
        {name}
      </div>
    )
  })
  const persistRecentDeck = (deck) => {
    const nextRecentDecks = upsertRecentDeck(recentSheets, deck);
    window.localStorage.setItem(
      "recent",
      buildRecentDecksStorageValue(nextRecentDecks)
    );
    setRecentSheets(nextRecentDecks);
  };

  const createSpreadsheetAccessData = (spreadsheetId, accessToken) => {
    const accessData = {
      spreadsheetId,
      access_token: accessToken,
      local_user_id: userGuid,
    };

    accessData.refreshAccessToken = async () => {
      const refreshedSession = await refreshGoogleAccessToken({
        localUserId: userGuid,
      });
      accessData.access_token = refreshedSession.access_token;
      setSpreadsheetAccessData((currentAccessData = {}) => ({
        ...currentAccessData,
        access_token: refreshedSession.access_token,
        local_user_id: userGuid,
        refreshAccessToken: accessData.refreshAccessToken,
      }));
      return refreshedSession.access_token;
    };

    return accessData;
  };

  const loadSpreadsheet = async ({
    spreadsheetId,
    accessToken,
    deckTitle,
  }) => {
    setSpinnerActive(true);
    setLoadText("");
    try {
      if (!spreadsheetId) {
        setLoadText("Could not determine which spreadsheet to open.");
        return false;
      }

      const sAD = createSpreadsheetAccessData(spreadsheetId, accessToken);
      setSpreadsheetAccessData(sAD);
      const sheetData = await readSheetTitles(
        // avoid race condition
        sAD
      );
      if (!sheetData) {
        setLoadText("Could not read that Google Sheet. Check the selected file and permissions.");
        return false;
      }

      persistRecentDeck({
        title: sheetData.properties.title || deckTitle || "Untitled deck",
        spreadsheetId,
      });
      let proc1 = readCards(sheetData.sheets, sAD);
      let proc2 = readHistory(sheetData.sheets, sAD);
      let proc3 = readParams(sheetData.sheets, sAD, setParams);
      let [tcards, items, loadedParams] = await Promise.all([proc1, proc2, proc3]);
      let [historyDict, review_] = items;
      const syncedCards = await syncActiveColumnWithHistory(
        sAD,
        tcards,
        historyDict
      );
      setCards(syncedCards);
      setReview(review_);
      setHistory(historyDict)

      if (syncedCards && syncedCards.length > 0) {
        console.log('setting history etc')
        const persistOptimizedParams = async (nextParams) => {
          setParams(nextParams);
          try {
            await writeParamsSheet(sAD, nextParams);
          } catch (error) {
            console.error("Failed to persist params", error);
          }
        };
        cardSelector.setParams(loadedParams, persistOptimizedParams);
        cardSelector.setHistory(historyDict);
        cardSelector.cards = syncedCards;
        console.log('history is:' , cardSelector.history)
        cardSelector.printLSTMState()
        await cardSelector.ready();
        if (shouldTrainImmediately()) {
          console.info("[fsrs] train_immediately=1");
          await cardSelector.trainFsrsImmediately();
        }
        let {probability: pcorr, cardId: pcard } = await cardSelector.selectCard(
          review_,
          -1,
          0,
          loadedParams?.showBothSides ?? true
        );
        assert(syncedCards.filter(card => card.card_id.toString() === pcard.toString()).length > 0, `pcard is ${pcard}, ${syncedCards[0].card_id}`);
        setPreselectedCard(syncedCards.filter(card => card.card_id.toString() === pcard.toString())[0]);
        setPCorrect(pcorr);
        setScreen('reviews');
        return true;
      }

      setLoadText('No flashcards found. Add rows with front and back values in columns three and four.');
      setTryAgain(true);
      return false;
    } catch (error) {
      console.error('Failed to load Google Sheet', error);
      setLoadText('Could not load that Google Sheet right now.');
      return false;
    } finally {
      console.log('spinner inactive')
      setSpinnerActive(false);
    }
  }

  const openPicker = async (accessToken) => {
    if (!GOOGLE_PICKER_API_KEY) {
      setLoadText("Google Drive Picker is not configured yet. Set REACT_APP_GOOGLE_PICKER_API_KEY.");
      return;
    }

    try {
      await showSpreadsheetPicker({
        developerKey: GOOGLE_PICKER_API_KEY,
        appId: GOOGLE_APP_ID,
        oauthToken: accessToken,
        callback: async (pickerData) => {
          const action = pickerData?.[window.google?.picker?.Response?.ACTION];
          if (action === window.google?.picker?.Action?.PICKED) {
            const selectedDocument =
              pickerData?.[window.google.picker.Response.DOCUMENTS]?.[0];
            if (!selectedDocument) {
              setLoadText("Google Drive did not return a spreadsheet.");
              return;
            }

            const spreadsheetId =
              selectedDocument[window.google.picker.Document.ID];
            const deckTitle =
              selectedDocument[window.google.picker.Document.NAME];
            await loadSpreadsheet({
              spreadsheetId,
              accessToken,
              deckTitle,
            });
          }
        },
      });
    } catch (error) {
      console.error("Failed to open Google Picker", error);
      setLoadText("Could not open Google Drive right now.");
    }
  };

  const responseGoogle = async (response) => {
    const code = response?.code;
    if (!code) {
      setLoadText("Google did not return an authorization code.");
      return;
    }

    let authSession = null;
    try {
      authSession = await exchangeGoogleAuthCode({
        code,
        localUserId: userGuid,
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Failed to exchange Google authorization code", error);
      setLoadText("Could not start a Google Drive session right now.");
      return;
    }

    const accessToken = authSession.access_token;
    await continueWithAccessToken(accessToken);
  }
  const continueWithAccessToken = async (accessToken) => {
    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = { type: null, deck: null };

    if (pendingAction?.type === "recent" && pendingAction.deck?.spreadsheetId) {
      const loadedRecentDeck = await loadSpreadsheet({
        spreadsheetId: pendingAction.deck.spreadsheetId,
        accessToken,
        deckTitle: pendingAction.deck.title,
      });

      if (!loadedRecentDeck) {
        setLoadText("Reconnect this deck by choosing it again from Google Drive.");
        await openPicker(accessToken);
      }
      return;
    }

    await openPicker(accessToken);
  };

  const beginGoogleSession = async (pendingAction) => {
    pendingActionRef.current = pendingAction;
    setLoadText("");

    try {
      const refreshedSession = await refreshGoogleAccessToken({
        localUserId: userGuid,
        allowMissingRefreshToken: true,
      });
      if (refreshedSession?.access_token) {
        await continueWithAccessToken(refreshedSession.access_token);
        return;
      }
      if (refreshedSession?.missing_refresh_token) {
        login();
        return;
      }
    } catch (error) {
      console.info("Falling back to interactive Google login", error);
    }

    login();
  };
  const [slid, setSlid] = useState(false);

  const isSlidFirst = () => { 
    return slid ? ' slide-out' : ""}
  const isSlidSecond = () => { return slid ? ' slide-in' : ''}
  const login = useGoogleLogin({
    onSuccess: tokenResponse => responseGoogle(tokenResponse),
    onError: () => setLoadText("Google sign-in was cancelled or failed."),
    scope: GOOGLE_DRIVE_FILE_SCOPE,
    flow: "auth-code",
    ux_mode: "popup",
  });
  const startPickerFlow = () => {
    if (!userGuid) {
      setLoadText("Still initializing your local session. Try again in a second.");
      return;
    }
    beginGoogleSession({ type: "picker", deck: null });
  };
  return <>
    <div className="loader flex-center"> 
    <div className="do-stuff-box">
    <div className="slide-box">
      <div className={"slider first" + isSlidFirst()}>
        <div className="input-area">
          <div className="textbox">
            <div className="main-header">
              Quick Cards is free Google Sheet flashcards that adapts to you.
            </div>
            <br/>
            <span className="sub-header">
 Make a Google Sheet with two columns (label them <i>front</i> and <i>back</i>) and use our model to help you learn all the rows as flashcards. All your data is saved in your Google Sheet and everything runs on your computer.
            </span>
          </div>
          <div className="bottombox">
            <div className="blue-button" onClick={() => setSlid(true)}>
              Get started
            </div>
            <div className="priv-terms">
              <a href="/privacy">Privacy</a> and <a href="/terms">terms</a>.
            </div>
    </div>
    </div>
      </div>
      <div className={"slider second" + isSlidSecond()}>
        <div className="input-area">
          <div className="main-input-element">
            <div className="main-header">Choose a Google Sheet</div>
            <div className="picker-copy">
              Pick a spreadsheet from Google Drive. Quick Cards will only be able to
              access sheets you choose for the app.
            </div>
          </div>
          <div id="hello" >
            <GoogleButton
              props={{
                onClick: startPickerFlow,
                buttonIsEnabled: !!GOOGLE_PICKER_API_KEY,
              }}
            />
          </div>
          {loadText && (
            <div className="load-text">{loadText}</div>
          )}
          {!GOOGLE_PICKER_API_KEY && (
            <div className="load-text">
              Missing `REACT_APP_GOOGLE_PICKER_API_KEY`. Configure it before using Google Drive.
            </div>
          )}
          {recentSheetElements.length > 0 && (
            <>
            <h2>Your recent decks:</h2>
            {recentSheetElements}
            </>
          )
          }
        </div>
      { spinnerActive && 
        <Spinner/>
      }
      
      <div className="priv-terms" >
          <a href="/how">How does this work?</a>
      </div>
    </div>
    </div>
    </div>
  </div>

    </>
}
